import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  CommandPalette,
  type CommandItem,
  IconButton,
  MobileNavigation,
  Sidebar,
  StatusIndicator,
  type IntegrationState,
  ToastRegion,
} from '@luacentrix/ui';
import { useApp } from '../app-state';
import { AccountChip, AccountMenu } from '../components/AccountMenu';
import { BrandLogo } from '../components/BrandMark';
import { ThemePicker } from '../components/ThemePicker';
import {
  activeRouteId,
  DASHBOARD_BASE,
  NAV_ITEMS,
  readCollapsed,
  ROUTES,
  routeByIdValue,
  writeCollapsed,
} from '../app-nav';
import { useHealth, useUnreadNotificationCount } from '../data/queries';

const API_STATE_LABELS: Partial<Record<IntegrationState, string>> = {
  pending: 'API checking',
  connected: 'API online',
  unavailable: 'API degraded',
  error: 'API unreachable',
};

function healthState(health: {
  isPending: boolean;
  isError: boolean;
  data: { status: 'ok' | 'degraded' } | undefined;
}): IntegrationState {
  if (health.isPending) return 'pending';
  if (health.isError || health.data === undefined) return 'error';
  return health.data.status === 'ok' ? 'connected' : 'unavailable';
}

export function AppShell() {
  const { api, theme, toggleTheme, account } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const health = useHealth(api);
  const canReadSecurity = account.status === 'signed_in' && account.has('security.read');
  const alerts = useUnreadNotificationCount(api, canReadSecurity);
  const mainRef = useRef<HTMLElement>(null);

  const activeId = activeRouteId(location.pathname);
  const activeRoute = routeByIdValue(activeId);

  useEffect(() => {
    setMobileOpen(false);
    setPaletteOpen(false);
    mainRef.current?.focus();
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [location.pathname]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(current => {
      writeCollapsed(!current);
      return !current;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(current => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const commands = useMemo<Array<CommandItem>>(() => {
    const navigation: Array<CommandItem> = ROUTES.map(route => ({
      id: `go:${route.id}`,
      label: route.label,
      group: 'Navigate',
      icon: route.icon,
      keywords: [route.href, route.description],
    }));
    const actions: Array<CommandItem> = [
      {
        id: 'action:theme',
        label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Appearance',
        icon: theme === 'dark' ? 'sun' : 'moon',
      },
      {
        id: 'action:collapse',
        label: collapsed ? 'Expand sidebar' : 'Collapse sidebar',
        group: 'Appearance',
        icon: 'menu',
      },
      { id: 'action:refresh', label: 'Re-check API health', group: 'Platform', icon: 'refresh' },
    ];
    return [...navigation, ...actions];
  }, [collapsed, theme]);

  const runCommand = useCallback(
    (id: string) => {
      setPaletteOpen(false);
      if (id.startsWith('go:')) {
        const route = routeByIdValue(id.slice(3));
        if (route !== undefined) navigate(route.href);
        return;
      }
      if (id === 'action:theme') {
        toggleTheme();
        return;
      }
      if (id === 'action:collapse') {
        toggleCollapsed();
        return;
      }
      if (id === 'action:refresh') {
        void health.refetch();
      }
    },
    [health, navigate, toggleCollapsed, toggleTheme],
  );

  const selectNav = useCallback(
    (id: string) => {
      const route = routeByIdValue(id);
      navigate(route?.href ?? DASHBOARD_BASE);
    },
    [navigate],
  );

  const alertLabel = !canReadSecurity
    ? null
    : alerts.isError
      ? 'Security alerts unavailable'
      : alerts.data === undefined
        ? 'Checking security alerts'
        : alerts.data.unread_count === 0
          ? 'No unread security alerts'
          : `${alerts.data.unread_count} unread security alerts`;

  const openAlerts = (): void => {
    navigate('/security#security-notifications');
  };

  const sidebarHeader = (
    <div className="brand-lockup" data-brand-collapsed={String(collapsed)}>
      <BrandLogo size={collapsed ? 'sm' : 'lg'} />
    </div>
  );

  const mobileHeader = (
    <div className="brand-lockup" data-brand-collapsed="false">
      <BrandLogo size="lg" />
    </div>
  );

  const sidebarFooter = (
    <>
      {collapsed ? null : (
        <p className="sidebar-footer__hint">
          Data is read live from the developer API. Nothing here is simulated.
        </p>
      )}
      <div className="sidebar-footer__row">
        <IconButton
          name={collapsed ? 'arrowRight' : 'arrowLeft'}
          label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggleCollapsed}
        />
        {collapsed ? null : <Badge tone="neutral">{ROUTES.length} pages</Badge>}
      </div>
      <AccountChip collapsed={collapsed} />
    </>
  );

  return (
    <div className="shell">
      <div className="shell__sidebar-desktop">
        <Sidebar
          items={NAV_ITEMS}
          activeId={activeId}
          onSelect={selectNav}
          label="Primary"
          header={sidebarHeader}
          footer={sidebarFooter}
          collapsed={collapsed}
        />
      </div>
      <div className="shell__body">
        <header className="shell__topbar">
          <span className="shell__nav-toggle">
            <IconButton name="menu" label="Open navigation" onClick={() => setMobileOpen(true)} />
          </span>
          <span className="shell__brand">
            <BrandLogo size="sm" />
            <span className="shell__page-label">{activeRoute?.label ?? 'Dashboard'}</span>
          </span>
          <span className="shell__spacer" />
          <StatusIndicator
            state={healthState(health)}
            label={API_STATE_LABELS[healthState(health)] ?? 'API'}
          />
          {alertLabel === null ? null : (
            <span className="shell__alerts">
              <Button
                icon="bell"
                size="sm"
                variant={
                  alerts.data !== undefined && alerts.data.unread_count > 0 ? 'danger' : 'secondary'
                }
                onClick={openAlerts}
              >
                {alertLabel}
              </Button>
            </span>
          )}
          <span className="shell__search">
            <Button icon="search" size="sm" onClick={() => setPaletteOpen(true)}>
              Search
            </Button>
          </span>
          <ThemePicker />
          <AccountMenu />
        </header>
        <main ref={mainRef} tabIndex={-1} className="shell__main">
          <div className="shell__content" key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        items={commands}
        onClose={() => setPaletteOpen(false)}
        onSelect={runCommand}
        placeholder="Search pages and actions"
      />
      <MobileNavigation open={mobileOpen} label="Navigation" onClose={() => setMobileOpen(false)}>
        <Sidebar
          items={NAV_ITEMS}
          activeId={activeId}
          onSelect={selectNav}
          label="Mobile"
          header={mobileHeader}
          footer={<AccountChip />}
          collapsed={false}
        />
      </MobileNavigation>
      <ToastRegion />
    </div>
  );
}
